import { useMemo } from 'react';
import BackgroundDots from '../BackgroundDots';
import useLanguage from '../../../hooks/useLanguage';
import { getLegalContent } from '../../legal/legalContent';
import { LEGAL_PAGES, getLegalPath } from '../../legal/legalRoutes';
import './MobileLegalPage.css';

const MobileLegalPage = ({ pageKey, onNavigate }) => {
  const { language, t } = useLanguage();
  const content = useMemo(() => getLegalContent(language, pageKey), [language, pageKey]);

  const navItems = useMemo(() => (
    LEGAL_PAGES.map((page) => ({
      key: page.key,
      path: page.path,
      label: t(`legal.nav.${page.key}`, {
        defaultValue: page.key === 'terms' ? 'Terms of Service' : page.key === 'cookies' ? 'Cookie Policy' : page.key === 'notice' ? 'Legal Notice' : 'Privacy Policy'
      })
    }))
  ), [t]);

  const handleNavClick = (key) => {
    if (key === pageKey) {
      return;
    }
    if (typeof onNavigate === 'function') {
      onNavigate(key);
      return;
    }
    const target = getLegalPath(key);
    if (target && typeof window !== 'undefined') {
      window.location.href = target;
    }
  };

  const handleBack = () => {
    if (typeof window === 'undefined') return;
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.href = '/';
  };

  return (
    <div className="mobile-legal-layer">
      <BackgroundDots className="mobile-legal-background" />
      <div className="mobile-legal-shell">
        <header className="mobile-legal-header">
          <button type="button" className="mobile-legal-back" onClick={handleBack}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('legal.actions.back', { defaultValue: 'Back' })}
          </button>
          <a className="mobile-legal-app" href="/">
            {t('legal.actions.app', { defaultValue: 'Open app' })}
          </a>
        </header>

        <div className="mobile-legal-content">
          <div className="mobile-legal-title-block">
            <p className="mobile-legal-eyebrow">{t('legal.eyebrow', { defaultValue: 'Loopino legal' })}</p>
            <h1>{content.title}</h1>
            <p className="mobile-legal-summary">{content.summary}</p>
          </div>

          <nav className="mobile-legal-nav" aria-label={t('legal.nav.title', { defaultValue: 'Legal documents' })}>
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`mobile-legal-nav-button ${item.key === pageKey ? 'is-active' : ''}`}
                onClick={() => handleNavClick(item.key)}
                aria-current={item.key === pageKey ? 'page' : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="mobile-legal-card">
            <div className="mobile-legal-updated">
              {t('legal.updated', { defaultValue: 'Last updated: {{date}}', date: content.updated })}
            </div>
            {content.sections.map((section, index) => (
              <section key={`${section.title}-${index}`} className="mobile-legal-section">
                <h2>{section.title}</h2>
                {section.paragraphs?.map((paragraph, paragraphIndex) => (
                  <p key={`${section.title}-p-${paragraphIndex}`}>{paragraph}</p>
                ))}
                {section.bullets?.length ? (
                  <ul>
                    {section.bullets.map((bullet, bulletIndex) => (
                      <li key={`${section.title}-b-${bulletIndex}`}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLegalPage;
